import { Badge, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@commenda/kakashi';
import type { MembershipComputation } from './compute';
import { type OwnershipEntry, ownerDisplayName } from './types';

interface MembershipSummaryProps {
  ids: string[];
  entries: Record<string, OwnershipEntry>;
  computation: MembershipComputation;
  // ISO-4217 code shown in the capital column header.
  currency: string;
}

const fmtNum = (n: number) => n.toLocaleString('en-US');
const fmtPercent = (n: number) => `${n.toFixed(2)}%`;
const fmtMoney = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const MembershipSummary = ({ ids, entries, computation, currency }: MembershipSummaryProps) => {
  const {
    perOwnerUnits,
    perOwnerCapital,
    unitsOutstanding,
    totalCapitalAllocated,
    percentTotal,
    hasIssuedUnits,
    managerManaged,
  } = computation;
  // Member + (Role?) + % owned + (Units?) + Capital contribution.
  const colSpan = 3 + (managerManaged ? 1 : 0) + (hasIssuedUnits ? 1 : 0);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h3 className="text-base font-medium text-text-primary">Ownership Summary</h3>
        <p className="text-sm text-text-secondary">
          Units and capital contributions are calculated from each member&apos;s ownership percentage.
        </p>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Member</TableHead>
            {managerManaged ? <TableHead>Role</TableHead> : null}
            <TableHead className="text-right">% owned</TableHead>
            {hasIssuedUnits ? <TableHead className="text-right">Units</TableHead> : null}
            <TableHead className="text-right">Capital contribution ({currency})</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {ids.length === 0 ? (
            <TableRow>
              <TableCell colSpan={colSpan} className="py-6 text-center text-sm text-text-tertiary">
                No members added yet.
              </TableCell>
            </TableRow>
          ) : (
            ids.map((id, i) => {
              const entry = entries[id];
              if (!entry) return null;
              return (
                <TableRow key={id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{ownerDisplayName(entry.owner)}</span>
                      <Badge variant="gray" size="sm">
                        {entry.owner.kind === 'individual' ? 'Individual' : 'Entity'}
                      </Badge>
                    </div>
                  </TableCell>
                  {managerManaged ? (
                    <TableCell>
                      <Badge variant="gray" size="sm">
                        {entry.governanceRole === 'MANAGER' ? 'Manager' : 'Member'}
                      </Badge>
                    </TableCell>
                  ) : null}
                  <TableCell className="text-right">{fmtPercent(entry.ownershipPercent ?? 0)}</TableCell>
                  {hasIssuedUnits ? (
                    <TableCell className="text-right">{fmtNum(perOwnerUnits[i] ?? 0)}</TableCell>
                  ) : null}
                  <TableCell className="text-right">{fmtMoney(perOwnerCapital[i] ?? 0)}</TableCell>
                </TableRow>
              );
            })
          )}

          <TableRow className="txt-small-bold bg-bg-secondary">
            <TableCell>Total</TableCell>
            {managerManaged ? <TableCell /> : null}
            <TableCell className="text-right">{fmtPercent(percentTotal)}</TableCell>
            {hasIssuedUnits ? <TableCell className="text-right">{fmtNum(unitsOutstanding)}</TableCell> : null}
            <TableCell className="text-right">{fmtMoney(totalCapitalAllocated)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
};
